import React from 'react';
import { ErrorMessage } from 'formik';
import { Radio, Checkbox, Divider, Space, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  colors,
  StepTitle,
  InfoBox,
  FormSection,
  FormItem,
  SectionTitle,
  ErrorText,
  LargeCheckbox,
  LargeRadio,
  inlineStyles
} from '../styled';
import { AI_VOICE_CALL_CONSENT_TEXT } from '../constants';

const { Paragraph, Text } = Typography;

const StepConsents = ({ values, errors, touched, setFieldValue, isFieldVisible = () => true }) => {
  const hasConsentFields = isFieldVisible('mediaRelease') || isFieldVisible('endlessHealthAcknowledgment') || isFieldVisible('dataSharingPermission') || isFieldVisible('heartGuardChampionOptIn') || isFieldVisible('bhaWaiverConsent') || isFieldVisible('aiVoiceCallConsent');
  const hasOptionalFields = isFieldVisible('healthConcern') || isFieldVisible('healthSupport');

  return (
  <>
    <StepTitle level={4}>Consents & Acknowledgements</StepTitle>

    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={inlineStyles.infoParagraph}>
          Please review and complete the following consent sections.
        </Paragraph>
      </div>
    </InfoBox>

    {hasConsentFields && (
    <FormSection>
      {/* Media Release */}
      {isFieldVisible('mediaRelease') && (
      <FormItem>
        <SectionTitle>Media Release</SectionTitle>
        <Paragraph style={{ fontSize: '14px', color: colors.text, marginTop: '4px', marginBottom: '12px' }}>
          I acknowledge that, while participating in this screening, I may be photographed, and or videotaped.
          Black Heart Association, and partners, may use this material for promotional, educational and advertising purposes.
        </Paragraph>
        <div>
          <Radio.Group
            value={values.mediaRelease}
            onChange={(e) => setFieldValue('mediaRelease', e.target.value)}
          >
            <Space direction="vertical">
              <LargeRadio value="yes">I acknowledge</LargeRadio>
              <LargeRadio value="no">I do not consent</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="mediaRelease" component={ErrorText} />
      </FormItem>
      )}

      {/* Endless Health Testing Acknowledgment */}
      {isFieldVisible('endlessHealthAcknowledgment') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <div style={{ borderRadius: '8px', marginBottom: '16px' }}>
          <Paragraph style={{ fontSize: '14px', color: colors.text }}>
            Endless Health&apos;s test involves a thoroughly validated, high accuracy, prick testing, Tests are processed in an FDA-certified CLIA lab. Although it is a relatively safe procedure, there are some contraindications and situations where caution is required, check if any of these contraindication are a concern for you, otherwise speak with the organizers of the testing event: Hemophobia Hypotension or Shock Hemophilia Hyperkeratosis Skin Condition Cold Temperature Poor Circulation Burns or Scars Low Platelet Count Allergy to Cleaning Agents Dehydration Edema None of these concerns apply for me and I accept responsibility By participating in this lipid panel screen, you agree to abide by Endless Health Inc&apos;s terms and conditions, And agree to and acknowledge Endless Health&apos;s privacy policy, and agree to Endless Health&apos;s HIPAA notice of privacy practices. The most important parts of the terms and policy are that you acknowledge that you are engaging in a medical test that will generate a medical result, which will be ordered and reviewed by a physician, and returned to you in a secure web portal in accordance with HIPAA. Endless Health does not provide medical advice or medical care. It offers high quality laboratory testing and research-backed lifestyle suggestions for a heart healthy lifestyle. You should discuss your test results with your primary physician or care team.
          </Paragraph>
        </div>
        <div>
          <Radio.Group
            value={values.endlessHealthAcknowledgment}
            onChange={(e) => setFieldValue('endlessHealthAcknowledgment', e.target.value)}
          >
            <Space direction="vertical">
              <LargeRadio value="True">True</LargeRadio>
              <LargeRadio value="False">False</LargeRadio>
            </Space>
          </Radio.Group>
        </div>
        <ErrorMessage name="endlessHealthAcknowledgment" component={ErrorText} />
      </FormItem>
      </>
      )}

      {/* Data Sharing Permission */}
      {isFieldVisible('dataSharingPermission') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <Paragraph style={{ fontSize: '14px', color: colors.text }}>
          Data is essential for keeping our bus operational and plays a critical role in helping us potentially save lives. We would like your permission to use your data for our white paper, research, or policy/law development. We do not share any identifiable information, and rest assured, we will never sell your personal information. Do you also permit your anonymized data to be used in research to improve health outcomes in communities of color?
        </Paragraph>
        <div style={{ marginTop: '12px' }}>
          <Checkbox.Group
            value={values.dataSharingPermission ? [values.dataSharingPermission] : []}
            onChange={(checkedValues) => setFieldValue('dataSharingPermission', checkedValues[checkedValues.length - 1] || '')}
          >
            <Space direction="vertical">
              <LargeCheckbox value="Yes, I consent">Yes, I consent</LargeCheckbox>
              <LargeCheckbox value="No, I do not consent">No, I do not consent</LargeCheckbox>
            </Space>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="dataSharingPermission" component={ErrorText} />
      </FormItem>
      </>
      )}

      {/* Heart Guard Champion Program */}
      {isFieldVisible('heartGuardChampionOptIn') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <Paragraph style={{ fontSize: '14px', color: colors.text }}>
          Would you like to become a member of BHA&apos;s &quot;Heart Guard Champion&quot; advocacy program? This program is dedicated to the principles of justice, righteousness, and equity. Our mission is to combat injustice, inequity, and unrighteousness within the healthcare system and beyond. We invite you to join us in our fight by engaging in policy reform, addressing systemic issues and raising your voice for our patient community. Together, we can create a more just and equitable world. By joining, you&apos;ll also have the opportunity to receive personalized feedback based on your responses, along with ongoing health updates and resources from the Black Heart Association. Join Heart Guard today and be a part of the change!
        </Paragraph>
        <div style={{ marginTop: '12px' }}>
          <Checkbox.Group
            value={values.heartGuardChampionOptIn ? [values.heartGuardChampionOptIn] : []}
            onChange={(checkedValues) => setFieldValue('heartGuardChampionOptIn', checkedValues[checkedValues.length - 1] || '')}
          >
            <Space direction="vertical">
              <LargeCheckbox value="Sign me up!">Sign me up!</LargeCheckbox>
              <LargeCheckbox value="Not interested at this time">Not interested at this time</LargeCheckbox>
            </Space>
          </Checkbox.Group>
        </div>
        <ErrorMessage name="heartGuardChampionOptIn" component={ErrorText} />
      </FormItem>
      </>
      )}

      {/* BHA Consent & Preferences */}
      {isFieldVisible('bhaWaiverConsent') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <div style={inlineStyles.waiverContainer}>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            I understand that as part of the screening process, a small sample of blood will be collected either by venipuncture or finger prick.
          </Paragraph>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            <strong>Risks Acknowledgment:</strong> I am aware of the associated risks, which include but are not limited to:
          </Paragraph>
          <ul style={{ fontSize: '14px', color: colors.text, marginBottom: '12px', paddingLeft: '24px' }}>
            <li>Bleeding</li>
            <li>Vessel Injury</li>
            <li>Bruising</li>
            <li>Infection</li>
          </ul>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            <strong>Disclaimer of Relationship:</strong> I acknowledge that participation does not create a patient/doctor relationship with BHA, its affiliates, or any healthcare providers involved.
          </Paragraph>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            <strong>Emergency Authorization:</strong> In case of an emergency, I authorize BHA, its affiliates, or healthcare providers to arrange for necessary medical treatment. I agree to hold harmless all parties involved in the administration of such emergency care. I understand that I am responsible for any medical expenses related to the emergency treatments provided.
          </Paragraph>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            <strong>Minor Consent:</strong> For participants under the age of 18, a signature from a parent or guardian is required.
          </Paragraph>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            <strong>Communication Consent:</strong> By signing this form, I consent to receive periodic newsletters from BHA. I understand that these communications are part of my engagement with BHA and I can opt-out at any time.
          </Paragraph>
          <Paragraph style={{ fontSize: '14px', color: colors.text, marginBottom: '12px' }}>
            By typing my name below, I provide my consent for BHA to perform the screening as described above and waive any liability against BHA and their partners for the conduct of the screening.
          </Paragraph>
        </div>
        <LargeCheckbox
          checked={values.bhaWaiverConsent}
          onChange={(e) => setFieldValue('bhaWaiverConsent', e.target.checked)}
          style={{
            lineHeight: '1.5',
            color: touched.bhaWaiverConsent && errors.bhaWaiverConsent ? colors.error : colors.text
          }}
        >
          I accept the Terms and Conditions
        </LargeCheckbox>
        <ErrorMessage name="bhaWaiverConsent" component={ErrorText} />
      </FormItem>
      </>
      )}

      {isFieldVisible('aiVoiceCallConsent') && (
      <>
      <Divider style={{ margin: '32px 0' }} />
      <FormItem>
        <SectionTitle>Phone Call Consent</SectionTitle>
        <LargeCheckbox
          checked={!!values.aiVoiceCallConsent}
          onChange={(e) => setFieldValue('aiVoiceCallConsent', e.target.checked)}
          style={{ lineHeight: '1.5', color: colors.text }}
        >
          <span style={{ fontSize: '14px' }}>{AI_VOICE_CALL_CONSENT_TEXT}</span>
        </LargeCheckbox>
      </FormItem>
      </>
      )}
    </FormSection>
    )}

    {hasOptionalFields && (
    <>
    <Divider style={{ margin: '40px 0' }}>
      <Text style={{ color: colors.text, fontSize: '16px', fontWeight: 'bold' }}>Optional Questions</Text>
    </Divider>

    <FormSection>
      {isFieldVisible('healthConcern') && (
      <FormItem>
        <SectionTitle>What is your biggest concern about your health right now?</SectionTitle>
        <textarea
          value={values.healthConcern}
          onChange={(e) => setFieldValue('healthConcern', e.target.value)}
          placeholder="Type your response here..."
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '12px',
            fontSize: '14px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
        <ErrorMessage name="healthConcern" component={ErrorText} />
      </FormItem>
      )}

      {isFieldVisible('healthSupport') && (
      <FormItem>
        <SectionTitle>What would help you feel more supported in your health journey?</SectionTitle>
        <textarea
          value={values.healthSupport}
          onChange={(e) => setFieldValue('healthSupport', e.target.value)}
          placeholder="Type your response here..."
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '12px',
            fontSize: '14px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
        <ErrorMessage name="healthSupport" component={ErrorText} />
      </FormItem>
      )}
    </FormSection>
    </>
    )}
  </>
  );
};

export default StepConsents;
